import type { PlanetType } from "../entities";
import {
	type AsteroidSettings,
	type MapDef,
	MapFormatError,
	PLANET_TYPES,
	type PointDef,
} from "./map-def";

/**
 * Validates untrusted JSON (e.g. a fetched map file) into a MapDef.
 * Hand-written to avoid a runtime schema dependency; errors name the offending path.
 */
export function parseJsonMap(data: unknown): MapDef {
	const root = object(data, "map");
	const name = string(root.name, "map.name");
	const sizeObj = object(root.size, "map.size");
	const size = { w: positive(sizeObj.w, "map.size.w"), h: positive(sizeObj.h, "map.size.h") };

	const planets = array(root.planets, "map.planets").map((p, i) => {
		const where = `map.planets[${i}]`;
		const o = object(p, where);
		return {
			type: planetType(o.type, `${where}.type`),
			mass: positive(o.mass, `${where}.mass`),
			radius: positive(o.radius, `${where}.radius`),
			...point(o, where, size),
		};
	});

	const spawnPoints = array(root.spawnPoints, "map.spawnPoints").map((p, i) =>
		point(object(p, `map.spawnPoints[${i}]`), `map.spawnPoints[${i}]`, size),
	);
	if (spawnPoints.length === 0) throw new MapFormatError("map.spawnPoints: at least one required");

	const fuelItems = (
		root.fuelItems === undefined ? [] : array(root.fuelItems, "map.fuelItems")
	).map((f, i) => {
		const where = `map.fuelItems[${i}]`;
		const o = object(f, where);
		return { ...point(o, where, size), amount: positive(o.amount, `${where}.amount`) };
	});

	const asteroids = root.asteroids === undefined ? undefined : asteroidSettings(root.asteroids);
	return { name, size, planets, spawnPoints, fuelItems, ...(asteroids ? { asteroids } : {}) };
}

function asteroidSettings(v: unknown): AsteroidSettings | undefined {
	const o = object(v, "map.asteroids");
	const out: AsteroidSettings = {};
	for (const key of ["max", "perMinute", "speed"] as const) {
		if (o[key] === undefined) continue;
		const n = finite(o[key], `map.asteroids.${key}`);
		if (n < 0) throw new MapFormatError(`map.asteroids.${key}: must be ≥ 0`);
		if (key === "max" && !Number.isInteger(n)) {
			throw new MapFormatError("map.asteroids.max: expected a whole number");
		}
		out[key] = n;
	}
	return Object.keys(out).length > 0 ? out : undefined;
}

type Obj = Record<string, unknown>;

function object(v: unknown, where: string): Obj {
	if (typeof v !== "object" || v === null || Array.isArray(v)) {
		throw new MapFormatError(`${where}: expected an object`);
	}
	return v as Obj;
}

function array(v: unknown, where: string): unknown[] {
	if (!Array.isArray(v)) throw new MapFormatError(`${where}: expected an array`);
	return v;
}

function string(v: unknown, where: string): string {
	if (typeof v !== "string" || v.length === 0) {
		throw new MapFormatError(`${where}: expected a non-empty string`);
	}
	return v;
}

function finite(v: unknown, where: string): number {
	if (typeof v !== "number" || !Number.isFinite(v)) {
		throw new MapFormatError(`${where}: expected a number`);
	}
	return v;
}

function positive(v: unknown, where: string): number {
	const n = finite(v, where);
	if (n <= 0) throw new MapFormatError(`${where}: must be > 0`);
	return n;
}

function point(o: Obj, where: string, size: { w: number; h: number }): PointDef {
	const x = finite(o.x, `${where}.x`);
	const y = finite(o.y, `${where}.y`);
	if (x < 0 || x >= size.w || y < 0 || y >= size.h) {
		throw new MapFormatError(`${where}: (${x}, ${y}) is outside the world`);
	}
	return { x, y };
}

function planetType(v: unknown, where: string): PlanetType {
	if (!PLANET_TYPES.includes(v as PlanetType)) {
		throw new MapFormatError(`${where}: expected one of ${PLANET_TYPES.join(", ")}`);
	}
	return v as PlanetType;
}
